/**
 * Column geometry for the Availability grid, shared by the header, group rows and
 * person rows so every row's columns line up. Widths are layout constants, not
 * brand values.
 */
import { monthStartOf } from "@/lib/availability/months";
import { horizonEdgeMonth } from "./model";

export const NAME_COL_PX = 268;
export const NET_COL_PX = 176;
export const LAST_THREE_COL_PX = 112;
export const MONTH_COL_MIN_PX = 72;
const COLUMN_GAP_PX = 2;

export type ColumnEdge = "horizon" | "today" | null;

export interface GridLayout {
  /** Months shown as columns, in order (the window, plus actual months when asked for). */
  columns: readonly string[];
  /** The report window itself: what "Net in window" sums. */
  windowMonths: readonly string[];
  /** The three months before the window, for "Last 3 mo" (empty when that column is hidden). */
  lastThreeMonths: readonly string[];
  /** CSS `grid-template-columns`, identical on every row. */
  template: string;
  /** Minimum width of the whole grid so months never squash below readable. */
  minWidthPx: number;
  columnGapPx: number;
  today: string;
  currentMonth: string;
  planHorizon: string | null;
  /** The first month past the plan horizon, or null. */
  horizonEdge: string | null;
  compact: boolean;
}

export function buildLayout(input: {
  columns: readonly string[];
  windowMonths: readonly string[];
  lastThreeMonths: readonly string[];
  today: string;
  planHorizon: string | null;
  compact: boolean;
}): GridLayout {
  const { columns, lastThreeMonths } = input;
  const showLast = lastThreeMonths.length > 0;
  const template = [
    `${NAME_COL_PX}px`,
    `${NET_COL_PX}px`,
    showLast ? `${LAST_THREE_COL_PX}px` : null,
    `repeat(${columns.length}, minmax(${MONTH_COL_MIN_PX}px, 1fr))`,
  ]
    .filter((part): part is string => part !== null)
    .join(" ");
  return {
    columns,
    windowMonths: input.windowMonths,
    lastThreeMonths,
    template,
    minWidthPx:
      NAME_COL_PX +
      NET_COL_PX +
      (showLast ? LAST_THREE_COL_PX : 0) +
      columns.length * (MONTH_COL_MIN_PX + COLUMN_GAP_PX) +
      8,
    columnGapPx: COLUMN_GAP_PX,
    today: input.today,
    currentMonth: monthStartOf(input.today),
    planHorizon: input.planHorizon,
    horizonEdge: horizonEdgeMonth(input.planHorizon),
    compact: input.compact,
  };
}

/**
 * The line drawn at a column's left edge: a dashed lavender one before the first
 * month past the plan horizon, a solid brand one at the start of today's month when
 * past months are shown in front of it. Never on the first column.
 */
export function columnEdge(layout: GridLayout, month: string, index: number): ColumnEdge {
  if (index === 0) return null;
  if (month === layout.horizonEdge) return "horizon";
  const first = layout.columns[0];
  if (month === layout.currentMonth && first !== undefined && first < layout.currentMonth) {
    return "today";
  }
  return null;
}

/** Tailwind classes for a column edge. */
export function edgeClass(edge: ColumnEdge): string {
  if (edge === "horizon") return "border-l-2 border-dashed border-[color:var(--avail-horizon)]";
  if (edge === "today") return "border-l-2 border-solid border-primary";
  return "";
}
